import React, { useState, useEffect, useRef } from 'react';

const assets = [
  { name: 'BTC/USD', color: '#f7931a', short: 'BTC', icon: { bg: 'linear-gradient(135deg,#f9a825,#f7931a)', symbol: '₿', textColor: '#fff', fontSize: 13 } },
  { name: 'ETH/USD', color: '#627eea', short: 'ETH', icon: { bg: 'linear-gradient(135deg,#8b9cf4,#627eea)', symbol: 'Ξ', textColor: '#fff', fontSize: 13 } },
  { name: 'AAVE/USD', color: '#B6509E', short: 'AAVE', icon: { bg: 'linear-gradient(135deg,#d580c8,#B6509E)', symbol: 'Aa', textColor: '#fff', fontSize: 9 } },
  { name: 'DOGE/USD', color: '#C2A633', short: 'DOGE', icon: { bg: 'linear-gradient(135deg,#e8c84a,#C2A633)', symbol: 'Ð',  textColor: '#fff', fontSize: 14 } },
  { name: 'SOL/USD',  color: '#9945ff', short: 'SOL',  icon: { bg: 'linear-gradient(135deg,#c084fc,#9945ff)',  symbol: '◎',  textColor: '#fff', fontSize: 12 } },
  { name: 'ALGO/USD', color: '#aaaaaa', short: 'ALGO', icon: { bg: 'linear-gradient(135deg,#555,#222)',        symbol: 'AL', textColor: '#fff', fontSize: 9  } },
];

const types = ['BUY', 'SELL', 'HOLD'];

function useAnimatedValue(target, duration = 900) {
  const [val, setVal] = useState(0);
  const prev = useRef(0);
  useEffect(() => {
    const start = prev.current;
    const diff = target - start;
    const startTime = performance.now();
    let id;
    const tick = (now) => {
      const t = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setVal(Math.round(start + diff * eased));
      if (t < 1) id = requestAnimationFrame(tick);
      else prev.current = target;
    };
    id = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(id);
  }, [target, duration]);
  return val;
}

const SignalBar = ({ conf, color }) => {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setWidth(conf), 80);
    return () => clearTimeout(t);
  }, [conf]);
  return (
    <div className="h-[3px] rounded-full bg-[#1a1a1f] overflow-hidden mt-1.5">
      <div
        className="h-full rounded-full"
        style={{
          width: `${width}%`,
          background: color,
          transition: 'width 0.8s cubic-bezier(0.4,0,0.2,1)',
          boxShadow: `0 0 6px ${color}60`,
        }}
      />
    </div>
  );
};

const AssetCell = ({ asset, signal, isNew }) => {
  const dirColor = signal.type === 'BUY' ? '#38FE60' : signal.type === 'SELL' ? '#ff5252' : '#ffa726';
  const arrow = signal.type === 'BUY' ? '↑' : signal.type === 'SELL' ? '↓' : '→';
  const confVal = useAnimatedValue(signal.conf, 700);

  return (
    <div
      className="rounded-xl p-3 flex flex-col gap-1 transition-all duration-500"
      style={{
        background: isNew ? `${asset.color}08` : 'rgba(255,255,255,0.015)',
        border: `1px solid ${isNew ? asset.color + '20' : 'rgba(255,255,255,0.04)'}`,
      }}
    >
            <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
                    <div
            className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 font-bold"
            style={{
              background: asset.icon.bg,
              fontSize: asset.icon.fontSize,
              color: asset.icon.textColor,
              boxShadow: `0 0 8px ${asset.color}40`,
              lineHeight: 1,
            }}
          >
            {asset.icon.symbol}
          </div>
          <span className="text-[11px] font-semibold text-white">{asset.name}</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-[13px] font-bold" style={{ color: dirColor }}>{arrow}</span>
          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{
            color: dirColor,
            background: dirColor + '15',
          }}>{signal.type}</span>
        </div>
      </div>

            <SignalBar conf={signal.conf} color={dirColor} />
      <div className="flex justify-between items-center">
        <span className="text-[9px] text-[#444] font-mono">confidence</span>
        <span className="text-[10px] font-bold font-mono" style={{ color: dirColor }}>{confVal}%</span>
      </div>
    </div>
  );
};

const AIWidget = () => {
  const [assetSignals, setAssetSignals] = useState({});
  const [newAsset, setNewAsset] = useState(null);
  const [recentSignals, setRecentSignals] = useState([]);
  const [scanStep, setScanStep] = useState(0);
  const [overallScore, setOverallScore] = useState(82);
  const displayScore = useAnimatedValue(overallScore, 1000);

  const scanLabels = ['Scanning markets…', 'Pattern analysis…', 'Signal computation…', 'Updating signals…'];

  
  useEffect(() => {
    const init = {};
    assets.forEach(a => {
      init[a.name] = {
        type: types[Math.floor(Math.random() * 3)],
        conf: Math.floor(70 + Math.random() * 28),
      };
    });
    setAssetSignals(init);
  }, []);

  
  useEffect(() => {
    const t = setInterval(() => setScanStep(s => (s + 1) % scanLabels.length), 2200);
    return () => clearInterval(t);
  }, []);

  
  useEffect(() => {
    const t = setInterval(() => {
      const asset = assets[Math.floor(Math.random() * assets.length)];
      const newSig = {
        type: types[Math.floor(Math.random() * 3)],
        conf: Math.floor(68 + Math.random() * 30),
      };
      setAssetSignals(prev => ({ ...prev, [asset.name]: newSig }));
      setNewAsset(asset.name);
      setOverallScore(Math.floor(78 + Math.random() * 18));

      
      setRecentSignals(prev => [{
        id: Date.now(),
        asset,
        ...newSig,
        ago: 'just now',
      }, ...prev.slice(0, 2)]);

      setTimeout(() => setNewAsset(null), 1200);
    }, 2800);
    return () => clearInterval(t);
  }, []);

  const bullishCount = Object.values(assetSignals).filter(s => s.type === 'BUY').length;

  return (
    <div className="w-full h-full flex flex-col rounded-2xl overflow-hidden border border-[#1a1a1f]"
      style={{ background: '#0c0c0e' }}>

            <div className="px-4 pt-4 pb-3 flex items-center justify-between border-b border-[#141418]">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-[#38FE60] animate-pulse"
            style={{ boxShadow: '0 0 8px rgba(56,254,96,0.6)' }} />
          <span className="text-white text-sm font-semibold">AI Engine</span>
          <span className="text-[#38FE60] text-[10px] font-mono opacity-70">{scanLabels[scanStep]}</span>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full"
            style={{ background: 'rgba(56,254,96,0.07)', border: '1px solid rgba(56,254,96,0.12)' }}>
            <span className="text-[#38FE60] text-sm font-bold font-mono">{displayScore}</span>
            <span className="text-[9px] text-[#38FE60] opacity-60 font-mono">/ 100</span>
          </div>
          <span className="text-[9px] text-[#333] font-mono">v3.2</span>
        </div>
      </div>

            <div className="p-3 grid grid-cols-2 gap-2">
        {assets.map(asset => (
          <AssetCell
            key={asset.name}
            asset={asset}
            signal={assetSignals[asset.name] || { type: 'HOLD', conf: 50 }}
            isNew={newAsset === asset.name}
          />
        ))}
      </div>

            <div className="mx-3 h-px bg-[#141418]" />

            <div className="px-3 pt-2.5 pb-1 flex-1">
        <div className="flex items-center justify-between mb-2 px-1">
          <span className="text-[9px] text-[#444] font-mono uppercase tracking-wider">Recent Signals</span>
          <div className="flex items-center gap-1">
            <div className="w-1 h-1 rounded-full bg-[#38FE60] animate-pulse" />
            <span className="text-[9px] text-[#38FE60] font-mono">live feed</span>
          </div>
        </div>
        <div className="space-y-1">
          {recentSignals.slice(0, 5).map((sig, i) => {
            const sigColor = sig.type === 'BUY' ? '#38FE60' : sig.type === 'SELL' ? '#ff5252' : '#ffa726';
            const times = ['just now', '3s ago', '8s ago', '14s ago', '22s ago'];
            return (
              <div key={sig.id}
                className="flex items-center gap-2 px-2.5 py-2 rounded-lg transition-all duration-500"
                style={{
                  background: i === 0 ? `${sigColor}08` : 'rgba(255,255,255,0.015)',
                  border: `1px solid ${i === 0 ? sigColor + '18' : 'rgba(255,255,255,0.03)'}`,
                  opacity: 1 - i * 0.12,
                }}>
                                <div className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 text-[8px] font-black text-white"
                  style={{ background: sig.asset.icon.bg, fontSize: sig.asset.icon.fontSize * 0.75 }}>
                  {sig.asset.icon.symbol}
                </div>
                                <span className="text-[11px] text-white font-medium flex-1">{sig.asset.name}</span>
                                <span className="text-[9px] text-[#333] font-mono">{times[i]}</span>
                                <span className="text-[9px] font-bold px-2 py-0.5 rounded"
                  style={{ color: sigColor, background: sigColor + '15', border: `1px solid ${sigColor}25` }}>
                  {sig.type}
                </span>
                                <span className="text-[9px] font-mono w-7 text-right" style={{ color: sigColor }}>{sig.conf}%</span>
              </div>
            );
          })}
        </div>
      </div>

            <div className="mx-3 h-px bg-[#141418]" />
      <div className="px-4 py-2.5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-[9px] text-[#444] font-mono uppercase tracking-wider">Market Bias</span>
          <div className="flex items-center gap-1.5">
            <div className="w-1.5 h-1.5 rounded-full"
              style={{ background: bullishCount >= 3 ? '#38FE60' : '#ff5252' }} />
            <span className="text-[11px] text-white font-semibold">
              {bullishCount >= 4 ? 'Strongly Bullish' : bullishCount >= 3 ? 'Bullish' : bullishCount >= 2 ? 'Neutral' : 'Bearish'}
            </span>
          </div>
        </div>
        <span className="text-[9px] text-[#333] font-mono">Binance · AI v3.2</span>
      </div>
    </div>
  );
};

export default AIWidget;
